import { useEffect, useRef } from 'react';
import { ChatMessage } from './ChatMessage';
import type { Message } from '@/lib/types';

interface ChatMessageListProps {
  messages: Message[];
  streamingMessage?: string;
  isLoading?: boolean;
}

export function ChatMessageList({
  messages,
  streamingMessage,
  isLoading = false,
}: ChatMessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4">
      {messages.length === 0 && !isLoading && (
        <div className="text-center text-slate-500 dark:text-slate-400 mt-8">
          <p className="text-sm">Start a conversation to explore the content!</p>
        </div>
      )}

      {messages.map((message, idx) => (
        <ChatMessage key={`${message.role}-${idx}-${message.content.slice(0, 20)}`} message={message} />
      ))}

      {/* Only show streaming message if it's not already in messages (to avoid duplicates) */}
      {streamingMessage && !messages.some(m => m.role === 'assistant' && m.content === streamingMessage) && (
        <ChatMessage
          message={{ role: 'assistant', content: streamingMessage }}
          isStreaming={true}
        />
      )}

      {isLoading && !streamingMessage && (
        <div className="flex justify-start mb-4">
          <div className="bg-slate-100 dark:bg-slate-800 rounded-lg px-4 py-3">
            <div className="flex gap-1">
              <div className="w-2 h-2 accent-bg rounded-full animate-typing-dot"></div>
              <div className="w-2 h-2 accent-bg rounded-full animate-typing-dot" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 accent-bg rounded-full animate-typing-dot" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
